/**
 * Token and authentication management utility functions
 */
import { HttpClient } from "@/api/services/HttpClient";

const BETTER_AUTH_SESSION_COOKIE = "better-auth.session_token";
const BETTER_AUTH_JWT_COOKIE = "better-auth.session_jwt";
let inMemorySessionToken: string | null = null;

export const isLikelyJWT = (token?: string | null): token is string => {
  return typeof token === "string" && token.split(".").length === 3;
};

// Cookie management functions
const getCookie = (name: string): string => {
  if (typeof document === "undefined") {
    return "";
  }

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift() || "";
  return "";
};

const setCookie = (name: string, value: string, exdays: number = 30): void => {
  const d = new Date();
  d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
  const expires = `expires=${d.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/`;
};

// Logout handler
export const handleLogout = async (): Promise<void> => {
  try {
    // Clear all localStorage data
    localStorage.clear();

    // Clear cookies
    setCookie('token', '', -1);
    setCookie('userId', '', -1);
    setCookie('workspaceId', '', -1);
    setCookie(BETTER_AUTH_JWT_COOKIE, '', -1);
    inMemorySessionToken = null;

    // Clear HttpClient token cache
    if (HttpClient && typeof HttpClient.setGetTokenFunction === 'function') {
      // Clear the token cache by setting a null function
      HttpClient.setGetTokenFunction(async () => null);
    }

    console.log('Logout completed - all auth data cleared');
  } catch (error) {
    console.error('Error during logout:', error);
  }
};

// Set Auth Token - This function is now deprecated since we use Better Auth
export const handleSetToken = (token: string): boolean => {
  // This function is kept for backward compatibility but does nothing
  // since tokens are now managed by Better Auth and HttpClient
  console.warn('handleSetToken is deprecated - tokens are now managed by Better Auth');
  return true;
};

// Check if user is authenticated - now checks for Better Auth data
export const isAuthenticated = (): boolean => {
  try {
    const userId = localStorage.getItem("userId");
    const workspaceId = localStorage.getItem("workspaceId");
    const userEmail = localStorage.getItem("userEmail");

    return !!(userId && workspaceId && userEmail);
  } catch (error) {
    console.error("Error checking authentication:", error);
    return false;
  }
};

/**
 * Persist the JWT we receive from Better Auth in memory so API clients
 * can retrieve it without re-fetching session data.
 */
export const setBetterAuthSessionToken = (token: string | null): void => {
  if (token && !isLikelyJWT(token)) {
    inMemorySessionToken = null;
    setCookie(BETTER_AUTH_JWT_COOKIE, '', -1);
    return;
  }
  inMemorySessionToken = token || null;
  if (token) {
    setCookie(BETTER_AUTH_JWT_COOKIE, encodeURIComponent(token));
  } else {
    setCookie(BETTER_AUTH_JWT_COOKIE, '', -1);
  }
};

/**
 * Read the Better Auth session token from memory or fall back to the
 * opaque cookie (legacy behavior for backwards compatibility).
 */
export const getBetterAuthSessionToken = (): string | null => {
  if (isLikelyJWT(inMemorySessionToken)) {
    return inMemorySessionToken;
  }

  const jwtCookie = getCookie(BETTER_AUTH_JWT_COOKIE);
  if (jwtCookie) {
    try {
      const decodedJwt = decodeURIComponent(jwtCookie);
      if (isLikelyJWT(decodedJwt)) {
        return decodedJwt;
      }
    } catch {
      if (isLikelyJWT(jwtCookie)) {
        return jwtCookie;
      }
    }
  }

  const rawToken = getCookie(BETTER_AUTH_SESSION_COOKIE);
  if (!rawToken) {
    return null;
  }

  try {
    const decoded = decodeURIComponent(rawToken);
    return isLikelyJWT(decoded) ? decoded : null;
  } catch {
    return isLikelyJWT(rawToken) ? rawToken : null;
  }
};

// Get auth token - now returns the session token if available
export const getAuthToken = (): string => {
  return getBetterAuthSessionToken() || "";
};

export const getAuthTokenAsync = async (): Promise<string | null> => {
  return getBetterAuthSessionToken();
};

// Check if token is expired - simplified since we don't manage tokens
export const isTokenExpired = (): boolean => {
  // Since we don't store tokens in localStorage anymore,
  // token expiration is handled by Better Auth and HttpClient
  return false;
};

// Workspace ID Management - Use localStorage
export const setWorkspaceId = (id: string): void => {
  if (id) {
    localStorage.setItem("workspaceId", id);
  }
};

export const getWorkspaceId = (): string => {
  return localStorage.getItem("workspaceId") || "";
};

// Role Checks
export const isOrganizationAdmin = (): boolean => localStorage.getItem("role") === "ORGANIZATION_ADMIN";
export const isWorkspaceAdmin = (): boolean => localStorage.getItem("role") === "WORKSPACE_ADMIN";
export const isWorkspaceAgent = (): boolean => localStorage.getItem("role") === "WORKSPACE_AGENT";

// Get User ID
export const getUserId = (): string | null => localStorage.getItem("userId");

if (HttpClient && typeof HttpClient.setGetTokenFunction === "function") {
  HttpClient.setGetTokenFunction(async () => getBetterAuthSessionToken());
}
